import { useEffect, useRef } from 'react';

import { useColorScheme } from '@/hooks/useColorScheme';
import { cn } from '@/utils/tailwind';

export function EditableText({
  value,
  onEdit,
  onSubmit,
  isEditing,
  setIsEditing,
  className,
}: {
  value: string;
  onEdit: (value: string) => void;
  onSubmit: (value: string) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  className?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { isDarkMode } = useColorScheme();

  // Focus input when isEditable becomes true
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleBlur = () => {
    if (isEditing) {
      onSubmit(value);
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSubmit(value);
      setIsEditing(false);
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      onEdit(value);
      setIsEditing(false);
      inputRef.current?.blur();
    }
  };

  return (
    <input
      ref={inputRef}
      value={value}
      onChange={(e) => onEdit(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={cn(
        isEditing ? 'rounded-md bg-background' : 'bg-transparent',
        className
      )}
      style={{
        borderRadius: '6px',
        border: isEditing
          ? isDarkMode
            ? '2px solid #2F6868'
            : '2px solid #2F6868'
          : '2px solid transparent',
        padding: isEditing ? '4px 12px' : '4px',
      }}
      readOnly={!isEditing}
    />
  );
}
